
import { useState, useEffect } from "react";
import { mockApps, appStoreCatalog, macOSCatalog, getAppsByCustomer } from "@/data/mockApps";
import { Application } from "@/types/application";
import { toast } from "sonner";

export const useApplications = (customerId?: string) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        // In a real implementation, this would be an API call
        const apps = customerId 
          ? getAppsByCustomer(customerId) 
          : mockApps;
        
        setApplications(apps);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to fetch applications"));
        setLoading(false);
      }
    };

    fetchApplications();
  }, [customerId]);

  const addApplication = async (app: Omit<Application, "id">): Promise<Application> => {
    try {
      // In a real implementation, this would be an API call
      const newApp: Application = {
        ...app,
        id: `app${mockApps.length + 1}`,
      };
      
      // Update the local state
      setApplications(prev => [...prev, newApp]);
      
      toast.success("Application added successfully");
      return newApp;
    } catch (error) {
      toast.error("Failed to add application");
      throw error;
    }
  };

  const updateApplication = async (updatedApp: Application): Promise<Application> => {
    try {
      // In a real implementation, this would be an API call
      const updatedApps = applications.map(app => 
        app.id === updatedApp.id ? updatedApp : app
      );
      
      setApplications(updatedApps);
      toast.success("Application updated successfully");
      return updatedApp;
    } catch (error) {
      toast.error("Failed to update application");
      throw error;
    }
  };

  const removeApplication = async (appId: string): Promise<void> => {
    try {
      // In a real implementation, this would be an API call
      const filteredApps = applications.filter(app => app.id !== appId);
      setApplications(filteredApps);
      toast.success("Application removed successfully");
    } catch (error) {
      toast.error("Failed to remove application");
      throw error;
    }
  };

  const getAppStoreCatalog = () => appStoreCatalog;
  const getMacOSCatalog = () => macOSCatalog;

  return {
    applications,
    loading,
    error,
    addApplication,
    updateApplication,
    removeApplication,
    getAppStoreCatalog,
    getMacOSCatalog
  };
};
